/**
 * Client-side types of the AI connection contract
 * (`docs/agents/handoffs/AI-CONNECT-001.md`, pull request #336, ERP-277 /
 * AI-BILL-005). Base path of the real API: `/t/{slug}/api/v1/ai`.
 *
 * The contract is not merged into `main` yet; these types follow that
 * document and must be re-checked against the merged handoff. Rules carried
 * over from it:
 *   - money is integer toman in a string; credit counters are decimal strings;
 *   - unknown values are `null`, never `"0"`;
 *   - a quote is not a hold - reserve happens inside `POST /runs`;
 *   - the UI switches on `error.details[0].code` and shows
 *     `error.details[0].message`; it never parses `error.message`.
 */

export const AI_FEATURE_ERP_ANALYSIS = 'ai.chat.erp-analysis';
export const AI_TOOL_INVENTORY_STOCK = 'inventory.stock.summary';

export const AI_DOMAINS = [
  'inventory',
  'sales',
  'procurement',
  'finance',
  'projects',
  'sakhtar',
] as const;
export type AiDomain = (typeof AI_DOMAINS)[number];

export const AI_TOOLS = [
  'inventory.stock.summary',
  'crm.opportunity.open',
  'procurement.order.open',
  'accounting.open-items',
  'projects.task.overdue',
  'sakhtar.composite',
] as const;
export type AiTool = (typeof AI_TOOLS)[number];

/** Integer-string micro-credits with its toman display value. */
export interface AiMoney {
  readonly microCredits: string;
  /** `null` when below one toman (suppressed) or when no conversion is stored. */
  readonly toman: string | null;
  /** `true` = positive amount that rounds below one toman → «کمتر از ۱ تومان». */
  readonly suppressedBelowOneToman: boolean;
}

export interface AiConversion {
  readonly microCreditsPerToman: string;
  readonly version: string;
  readonly rounding: 'half_up';
}

export interface AiBalance {
  readonly unit: 'toman';
  /** `null` = no stored conversion → «مبلغ تومان هنوز تعیین نشده». */
  readonly conversion: AiConversion | null;
  readonly available: AiMoney;
  readonly reserved: AiMoney;
  readonly amountToman: string | null;
  readonly amountLabel: string | null;
  /** Present only for a published flat raw-token rate with exact wallet precision. */
  readonly rawTokens?: {
    readonly available: string;
    readonly reserved: string;
    readonly tokensPerToman: string;
    readonly counting: 'input_plus_output_once';
  } | null;
}

export interface AiCreditPackage {
  readonly id: string;
  readonly title: string;
  readonly amountToman: string;
  readonly amountRial: string;
  readonly amountMicroCredits: string;
  readonly description: string;
  readonly badge: string | null;
  /** Exact raw-token quota; absent/null for legacy cost-based credit purchases. */
  readonly tokenCount?: string | null;
}

export interface AiWalletPackagesResponse {
  readonly packages: readonly AiCreditPackage[];
  readonly pricingMode?: 'raw_tokens' | 'cost_plus';
  readonly catalogVersion?: number;
  readonly customAmountAllowed?: boolean;
  readonly bounds: {
    readonly minToman: string;
    readonly maxToman: string;
    readonly conversionRate: string;
  };
}

export interface AiTopupCheckoutRequest {
  /** Cost-plus wallets only. A raw-token wallet orders a fixed package by `packageId`. */
  readonly amountToman?: string;
  readonly packageId?: string;
  readonly callbackUrl?: string;
}

export interface AiTopupCheckoutResponse {
  readonly orderId: string;
  readonly authority: string;
  readonly paymentUrl: string;
  readonly amountToman: string;
  readonly amountRial: string;
  readonly amountMicroCredits: string;
  readonly conversionRate: string;
}

export interface AiTopupVerifyResponse {
  readonly orderId: string;
  readonly status: 'paid' | 'failed' | 'cancelled' | 'pending';
  readonly message: string;
  readonly refId: string | null;
  readonly amountToman: string | null;
  readonly amountMicroCredits: string | null;
  /** Exact tokens the verified order issued (ADR-021); absent for legacy receipts. */
  readonly tokenQuota?: string | null;
  readonly alreadyVerified: boolean;
}

export interface AiTopupOrder {
  readonly orderId: string;
  readonly amountToman: string;
  readonly amountRial: string;
  readonly amountMicroCredits: string;
  readonly conversionRate: string;
  /** ADR-021 package sold by this order and its frozen quota; null for legacy orders. */
  readonly packageId: string | null;
  readonly tokenQuota: string | null;
  readonly authority: string | null;
  readonly status: 'pending' | 'paid' | 'failed' | 'cancelled';
  readonly refId: string | null;
  readonly cardPan: string | null;
  readonly createdAt: string;
  readonly settledAt: string | null;
  readonly errorMessage: string | null;
}

export type AiBudgetScope = 'tenant' | 'user' | 'feature' | 'automation';

export interface AiBudget {
  readonly scope: AiBudgetScope;
  readonly scopeKey: string;
  readonly period: { readonly start: string; readonly end: string; readonly zone: string };
  readonly hard: AiMoney;
  readonly soft: AiMoney;
  readonly consumed: AiMoney;
  readonly openHolds: AiMoney;
  readonly remaining: AiMoney;
  readonly hardCredits: string;
  readonly softCredits: string;
}

export interface AiBudgetUpdate {
  readonly scope: AiBudgetScope;
  readonly scopeKey: string;
  readonly hardCredits: string;
  readonly softCredits: string;
}

export interface AiFeatureStatus {
  readonly code: string;
  readonly implemented: boolean;
  readonly executable: boolean;
}

export interface AiActivation {
  readonly active: boolean;
  readonly optedIn: boolean;
  readonly killSwitch: boolean;
  readonly tenantActive: boolean;
  readonly features: readonly AiFeatureStatus[];
}

/** `GET /settings`. Writes still accept only `retentionDays`. */
export interface AiSettings {
  readonly optedIn: boolean;
  readonly retentionDays: number;
  readonly killSwitch: boolean;
  readonly chatProviderCode: string | null;
  readonly chatModelCode: string | null;
  readonly chatModelVersion: number | null;
  readonly importMappingFree: true;
  readonly importMappingCustomerChargeToman: '0';
}

export interface AiQuote {
  readonly quoteId: string;
  readonly expiresAt: string;
  readonly featureCode: string;
  /** `null` when the data is insufficient: no model was selected for the quote. */
  readonly providerCode: string | null;
  readonly modelCode: string | null;
  readonly modelVersion: string | null;
  readonly dataSufficient: boolean;
  readonly amountToman: string | null;
  readonly amountLabel: string | null;
  readonly customerMicroCredits: string;
  readonly scopeFingerprint: string;
  readonly message: string | null;
  /** A quote never calls the provider. */
  readonly providerCalled: false;
  readonly tool?: string;
  readonly domain?: string;
  /** Answer tokens the quote reserves; absent from servers before the auto-run tier. */
  readonly outputTokens?: number;
  readonly reserveInputTokens?: number;
  readonly reserveOutputTokens?: number;
  readonly estimatedTotalTokens?: number;
  /**
   * The workflow estimate may exceed 20,000 tokens, so the user confirms first.
   */
  readonly confirmationRequired: boolean;
  readonly deepSearch?:
    | {
        readonly enabled: boolean;
        readonly scope: 'web' | 'company' | 'hybrid';
      }
    | undefined;
}

export interface AiQuoteRequest {
  readonly question: string;
  readonly tool?: string;
  readonly domain?: string;
  readonly conversationId?: string;
  readonly period?: string;
  readonly bookId?: string;
  readonly deepSearch?:
    | {
        readonly enabled: boolean;
        readonly scope?: 'web' | 'company' | 'hybrid' | undefined;
      }
    | undefined;
}

export interface AiRunRequest {
  readonly quoteId: string;
  readonly idempotencyKey: string;
}

/** Backend `UsageState`; labels: در انتظار / تسویه‌شده / ناموفق / برگشت‌شده. */
export type AiRunState = 'pending' | 'settled' | 'failed' | 'refunded';

export interface AiUsageTokens {
  readonly inputTokens: string | null;
  readonly cachedInputTokens: string | null;
  readonly outputTokens: string | null;
  readonly reasoningTokens: string | null;
}

export interface AiEvidence {
  readonly entity: string;
  readonly recordId: string;
  /** Warehouse or item display name when the server sent one. */
  readonly label?: string;
  readonly onHand?: string;
  readonly amountRial?: string;
  readonly openBalanceRial?: string;
  readonly projectRef?: string | null;
  readonly side?: 'receivable' | 'payable';
  readonly stage?: string | null;
  readonly status?: string;
  readonly dueDate?: string;
  readonly daysOverdue?: number;
  readonly unit: 'quantity' | 'rial' | 'days';
  readonly extractedAt: string;
  readonly source: string;
  /** Server-computed fact metadata; never extracted from the model's prose. */
  readonly group?: string;
  readonly headline?: boolean;
  readonly kpi?: { readonly label: string; readonly value: string };
  readonly chart?: {
    readonly title: string;
    readonly label: string;
    readonly value: number;
    readonly unit: string;
  };
}

export interface AiKpi {
  readonly id?: string;
  readonly label: string;
  readonly value: string;
  readonly unit?: string;
  readonly hint?: string;
}

export interface AiChartItem {
  readonly label: string;
  readonly value: number;
  readonly formattedValue?: string;
  readonly unit?: string;
}

export interface AiChart {
  readonly type: 'bar' | 'pie' | 'line' | 'donut' | 'metric' | 'horizontal-bar';
  readonly title: string;
  readonly unit?: string;
  readonly items: readonly AiChartItem[];
  readonly categories?: readonly string[];
  readonly series?: readonly { readonly name: string; readonly data: readonly number[] }[];
}

/** Contract-Version this file was written against, and the head it was read at. */
export const CONTRACT_VERSION = 'AI-CONNECT-001.4';
export const CONTRACT_HEAD = 'a136223451883c94bf72109d8afd9b6c6404fef3';

/** Always four keys; each counter is a decimal string or `null` (unknown is never "0"). */
export interface AiTurn {
  readonly turnId: string;
  /** `null` when there was no stock (nothing ran, nothing reserved). */
  readonly runId: string | null;
  readonly quoteId: string | null;
  /** `null` when no run row exists. */
  readonly correlationId: string | null;
  readonly question?: string | null;
  readonly state: AiRunState;
  readonly stateLabel: string;
  readonly domain: AiDomain;
  readonly tool: string;
  readonly answer: string | null;
  readonly evidence: readonly AiEvidence[];
  readonly usage: AiUsageTokens;
  readonly estimate: AiMoney | null;
  /** `null` while settlement is still open (reserved / usage unknown). */
  readonly charge: AiMoney | null;
  /** `null` while usage is unknown. */
  readonly amountToman: string | null;
  readonly amountLabel: string;
  readonly replayed: boolean;
  readonly budgetWarnings: readonly string[];
  readonly importMappingFree: true;
  readonly importMappingCustomerChargeToman: '0';
  readonly kpis?: readonly AiKpi[];
  readonly chart?: AiChart | null;
  readonly deepSearch?:
    | {
        readonly plan?:
          | {
              readonly subQueries: readonly string[];
              readonly steps: readonly {
                readonly id: string;
                readonly query: string;
                readonly sourceKind: 'web' | 'company' | 'document';
                readonly status: 'pending' | 'running' | 'completed' | 'failed';
                readonly resultCount: number;
                readonly summary?: string | undefined;
              }[];
              readonly status: string;
            }
          | undefined;
        readonly sources?:
          | readonly {
              readonly id: string;
              readonly kind: 'web' | 'erp' | 'document';
              readonly title: string;
              readonly url?: string | undefined;
              readonly recordId?: string | undefined;
              readonly snippet: string;
              readonly accessedAt: string;
              readonly publishedAt?: string | undefined;
            }[]
          | undefined;
        readonly contradictions?: readonly string[] | undefined;
      }
    | undefined;
}

/** Execution and settlement are separate (handoff «Run, turn, and usage»). */
export const EXECUTION_STATUSES = [
  'not_started',
  'reserved',
  'running',
  'completed',
  'failed',
  'cancelled',
  'reconciliation',
] as const;
export type AiExecutionStatus = (typeof EXECUTION_STATUSES)[number];

export const SETTLEMENT_STATUSES = ['reserved', 'usage_unknown', 'settled', 'released'] as const;
export type AiSettlementStatus = (typeof SETTLEMENT_STATUSES)[number];

export interface AiExecution {
  readonly status: AiExecutionStatus;
  /** True only after the provider call was marked started. */
  readonly providerAttempted: boolean;
}

export interface AiSettlement {
  readonly status: AiSettlementStatus;
  /** True only for `usage_unknown`. */
  readonly usagePending: boolean;
  /** The amount still reserved; always a money object. */
  readonly held: AiMoney;
  /** `null` while the status is `reserved` or `usage_unknown`. */
  readonly consumed: AiMoney | null;
  readonly released: AiMoney | null;
}

/** `GET /runs/:runId`. */
export interface AiRunStatus {
  readonly runId: string;
  /** `null` until the turn row exists. */
  readonly turnId: string | null;
  readonly quoteId: string | null;
  readonly state: AiRunState;
  readonly stateLabel: string;
  readonly execution: AiExecution;
  readonly settlement: AiSettlement;
  readonly usage: AiUsageTokens;
  readonly amountToman: string | null;
  readonly amountLabel: string;
  readonly featureCode: string;
}

/** `GET /runs/idempotency/:idempotencyKey` - the read for a lost `POST /runs` response. */
export interface AiRunRecovery {
  readonly idempotencyKey: string;
  /** `null` for a no-stock turn that never reserved. */
  readonly runId: string | null;
  /** `null` when the turn row is not stored yet. */
  readonly turnId: string | null;
  readonly state: AiRunState;
  readonly stateLabel: string;
  readonly execution: AiExecution;
  readonly settlement: AiSettlement;
  readonly usage: AiUsageTokens;
}

/** One `GET /usage` row. */
export interface AiUsageRow {
  readonly turnId: string;
  readonly runId: string | null;
  readonly quoteId: string | null;
  readonly correlationId: string | null;
  readonly createdAt: string;
  readonly execution: AiExecution;
  readonly settlement: AiSettlement;
  readonly state: AiRunState;
  readonly stateLabel: string;
  readonly usage: AiUsageTokens;
  readonly charge: AiMoney | null;
  readonly amountToman: string | null;
  readonly amountLabel: string;
}

/** `GET /usage` body is exactly `{ scope, items }`; `items` is newest first, at most 50. */
export interface AiUsageResponse {
  readonly scope: AiUsageScope;
  readonly items: readonly AiUsageRow[];
}

export type AiUsageScope = 'own' | 'all';

export interface AiSettingsUpdate {
  readonly retentionDays: number;
}

/** One entry of `error.details`. */
export interface AiErrorDetail {
  readonly path: string;
  readonly code: string;
  readonly message: string;
}

/**
 * `error.details[0].code` values the UI may branch on. They are the codes the
 * backend raises (`AiCreditError` in `packages/addons/ai-credit`, mapped by
 * `apps/api/src/ai/workspace.controller.ts` at the head recorded above) and
 * that the handoff names. Any other code is unknown to the UI: it shows the
 * server's `details[0].message` (or a generic message with the requestId).
 *
 * Mapping used by the backend (outer `error.code`, HTTP status):
 *   NOT_FOUND 404          QUOTE_NOT_FOUND, RUN_NOT_FOUND, TURN_NOT_FOUND
 *   FORBIDDEN 403          QUOTE_ACTOR_MISMATCH, TENANT_AI_DISABLED
 *   VALIDATION_FAILED 409  BUDGET_HARD_STOP, AUTOMATION_BUDGET_REQUIRED, QUOTE_EXPIRED,
 *                          QUOTE_SCOPE_CHANGED, IDEMPOTENCY_CONFLICT, INSUFFICIENT_AI_CREDIT
 *   VALIDATION_FAILED 422  every other AiCreditError (FEATURE_NOT_IMPLEMENTED,
 *                          BROWSER_ESTIMATE_REJECTED, SETTINGS_FIELD_FORBIDDEN, INVALID_*, …)
 * A missing permission is `FORBIDDEN 403` WITHOUT `details` (message = the permission code).
 */
export const CONFIRMED_ERROR_CODES = [
  'QUOTE_SCOPE_CHANGED',
  'QUOTE_EXPIRED',
  'QUOTE_NOT_FOUND',
  'QUOTE_ACTOR_MISMATCH',
  'INSUFFICIENT_AI_CREDIT',
  'BUDGET_HARD_STOP',
  'AUTOMATION_BUDGET_REQUIRED',
  'TENANT_AI_DISABLED',
  'FEATURE_NOT_IMPLEMENTED',
  'BROWSER_ESTIMATE_REJECTED',
  'SETTINGS_FIELD_FORBIDDEN',
  'IDEMPOTENCY_CONFLICT',
  'RUN_NOT_FOUND',
  'TURN_NOT_FOUND',
  'RETENTION_CANNOT_INCREASE',
  'INVALID_RETENTION',
  'INVALID_ACTIVATION',
] as const;

export type AiErrorCode = (typeof CONFIRMED_ERROR_CODES)[number];

/** Display texts of the backend's `AI_ERROR_DISPLAY` (used only when `details[0].message` is empty). */
export const AI_ERROR_MESSAGES: Readonly<Record<string, string>> = {
  QUOTE_SCOPE_CHANGED: 'محدوده داده انبار از زمان Quote تغییر کرده است.',
  QUOTE_EXPIRED: 'مهلت Quote تمام شده است.',
  QUOTE_NOT_FOUND: 'Quote پیدا نشد.',
  QUOTE_ACTOR_MISMATCH: 'این Quote برای شما نیست.',
  INSUFFICIENT_AI_CREDIT: 'مانده کیف پول برای این اجرا کافی نیست.',
  BUDGET_HARD_STOP: 'سقف بودجه این دوره پر شده است.',
  AUTOMATION_BUDGET_REQUIRED: 'این اتوماسیون بودجه مستقل ندارد.',
  TENANT_AI_DISABLED: 'دستیار برای این سازمان فعال نیست.',
  FEATURE_NOT_IMPLEMENTED: 'این حوزه هنوز قابل اجرا نیست.',
  SETTINGS_FIELD_FORBIDDEN: 'این تنظیم از اینجا قابل تغییر نیست.',
  IDEMPOTENCY_CONFLICT: 'این کلید برای درخواست دیگری استفاده شده است.',
  RUN_NOT_FOUND: 'اجرا پیدا نشد.',
  TURN_NOT_FOUND: 'پاسخ پیدا نشد.',
};

/** Answer text of a run with no stock (handoff «Run, turn, and usage»). */
export const AI_NO_DATA_MESSAGE = 'داده کافی موجود نیست';

/** Server receipt for the single lifetime company allowance; independent of token counts. */
export interface AiWelcomeCredit {
  readonly status: 'granted' | 'already_granted' | 'disabled' | 'configuration_required';
  readonly program: string;
  readonly amountToman: string | null;
  readonly amountRial: string | null;
  readonly amountMicroCredits: string | null;
  readonly tokens?: string | null;
  readonly conversionRate: string | null;
  readonly conversionVersion: number | null;
  readonly operationId: string | null;
  readonly bucketId: string | null;
  readonly grantedAt: string | null;
}
